import React, { useMemo } from 'react'

import { useFlags } from 'launchdarkly-react-client-sdk'
import { t } from 'src/translations'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'

import AnimatedSection from 'public/components/default_template/online_ordering/checkout/AnimatedSection'
import CheckoutSection from 'public/components/default_template/online_ordering/checkout/CheckoutSection'
import CustomerInfoSection from 'public/components/default_template/online_ordering/checkout/CustomerInfoSection'
import FreeDeliveryCheckoutAlert from 'public/components/default_template/online_ordering/checkout/FreeDeliveryCheckoutAlert'
import { PackagingOptions } from 'public/components/default_template/online_ordering/checkout/packaging/PackagingOptions'
import CreditCardFormSPI from 'public/components/default_template/online_ordering/checkout/payment/CreditCardFormSPI'
import OrderPrices from 'public/components/default_template/online_ordering/checkout/payment/OrderPrices'
import { AcceptedPaymentMethods } from 'public/components/default_template/online_ordering/checkout/payment/SavedCreditCards'
import { CreditCardSurchargeDisclaimer } from 'public/components/default_template/surcharges/CreditCardSurchargeDisclaimer'
import { useCart } from 'public/components/online_ordering/CartContext'
import { useCheckout } from 'public/components/online_ordering/CheckoutContext'
import { useCustomer } from 'public/components/online_ordering/CustomerContextCommon'
import {
  PaymentOption,
  usePayment
} from 'public/components/online_ordering/PaymentContext'

import DiscountsSection from './Discounts/DiscountsSection'
import Fundraising from './Fundraising'
import TipCheckoutSection from 'src/public/components/default_template/online_ordering/checkout/payment/TipCheckoutSection'
import { DiningOptionBehavior } from 'src/apollo/onlineOrdering'
import PaymentOptionsSection from 'public/components/default_template/online_ordering/checkout/payment/PaymentOptionsSection'
import { ShowForUS } from 'shared/components/common/show_for_us/ShowForUS'
import StayInTouchSection from 'public/components/default_template/online_ordering/checkout/StayInTouchSection'
import { useIsIntlRestaurant } from 'src/lib/js/hooks/useIsIntlRestaurant'

interface PaymentSectionProps {
  isExpanded: boolean
}

const PaymentSection = ({ isExpanded }: PaymentSectionProps) => {
  const { ooRestaurant } = useRestaurant()
  const { cart, surcharges } = useCart()
  const { customer } = useCustomer()
  const { paymentOption, fundraisingEnabled } = usePayment()
  const { setPackagingOptions, packagingOptions } = useCheckout()
  const { enabledPaymentOptions } = useCheckout()
  const isIntl = useIsIntlRestaurant()

  const isDeliveryOrder: boolean = useMemo(() => {
    return cart?.diningOptionBehavior === DiningOptionBehavior.Delivery
  }, [cart])

  if (!ooRestaurant || !cart) {
    return null
  }

  const canPayAtCheckout =
    !enabledPaymentOptions.has(PaymentOption.UponReceipt) ||
    enabledPaymentOptions.size > 1

  return (
    <>
      {customer && <CustomerInfoSection />}

      <CheckoutSection
        title={t('payment-section.payment')}
        secondaryHeaderContent={
          canPayAtCheckout ? <AcceptedPaymentMethods /> : undefined
        }
        className={
          paymentOption === PaymentOption.UponReceipt && !isIntl
            ? 'noSectionDivider'
            : undefined
        }
      >
        {canPayAtCheckout && (
          <CreditCardSurchargeDisclaimer
            className='checkoutSurchargeDisclaimer'
            surcharges={surcharges}
            restaurantName={ooRestaurant?.name}
            surchargingConfig={ooRestaurant?.surchargingConfig}
          />
        )}
        <PaymentOptionsSection />
        <ShowForUS>
          <CreditCardInfo paymentOption={paymentOption} />
        </ShowForUS>
      </CheckoutSection>

      {!customer && <StayInTouchSection />}

      <CheckoutSection title={t('payment-section.discounts')}>
        {isExpanded && <DiscountsSection />}
      </CheckoutSection>
      {!isDeliveryOrder && <TipCheckoutSection />}
      {ooRestaurant?.packagingConfig?.enabled && (
        <CheckoutSection title={t('payment-section.packaging-options')}>
          <PackagingOptions
            packagingConfig={ooRestaurant.packagingConfig}
            selectedPackagingOptions={packagingOptions}
            onPackagingOptionsUpdate={setPackagingOptions}
          />
        </CheckoutSection>
      )}
      {fundraisingEnabled && (
        <CheckoutSection>
          <FundraisingSection />
        </CheckoutSection>
      )}
      <FreeDeliveryCheckoutAlert />
      <OrderPrices canShowPaymentAdjustments={isExpanded} />
    </>
  )
}

const CreditCardInfo = ({
  paymentOption
}: {
  paymentOption: PaymentOption | null
}) => {
  const { ooToastCashSpend: ooToastCashSpendEnabled } = useFlags()
  const { orderTotal, enabledPaymentOptions } = useCheckout()

  return (
    <AnimatedSection expanded={paymentOption === PaymentOption.PayNow}>
      <CreditCardFormSPI
        allowToastCash={ooToastCashSpendEnabled}
        disableSPI={orderTotal <= 0}
        canPayAtRestaurant={enabledPaymentOptions.has(
          PaymentOption.UponReceipt
        )}
      />
    </AnimatedSection>
  )
}

const FundraisingSection = () => {
  const { ooRestaurant } = useRestaurant()
  const { cart } = useCart()
  const {
    tipAmount,
    fundraisingEnabled,
    setFundraisingAmount,
    fundraisingAmount
  } = usePayment()
  const fundraisingCampaign = ooRestaurant?.fundraisingConfig ?? undefined
  return (
    <Fundraising
      fundraisingCampaign={fundraisingCampaign}
      onChange={(amount: number) => setFundraisingAmount(amount)}
      orderTotal={(cart?.order?.totalV2 || 0) + tipAmount}
      fundraisingEnabled={fundraisingEnabled}
      fundraisingAmount={fundraisingAmount}
    />
  )
}

export default PaymentSection
